import { useState } from 'react';

export default function ContactForm({ variant = 'default', showTitle = true }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Navn: formData.name,
          Telefonnummer: formData.phone,
          Email: formData.email,
          Beskrivelse: formData.message,
          website_url: formData.website_url
        })
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }

      setStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: '',
        website_url: ''
      });
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Der skete en fejl. Ring gerne direkte til os på +45 48 79 68 16.');
    }
  };

  if (variant === 'underline') {
    return (
      <div className="w-full">
        {status === 'success' ? (
          <div className="bg-emerald-950/60 border border-emerald-500/40 rounded-2xl p-8 text-center backdrop-blur-sm">
            <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              <i className="fa-solid fa-check" aria-hidden="true"></i>
            </div>
            <h4 className="text-xl font-bold text-white">Tak for din henvendelse!</h4>
            <p className="text-emerald-200 text-sm mt-2">
              Vi har modtaget din besked og vender tilbage hurtigst muligt.
            </p>
            <button
              onClick={() => setStatus('idle')}
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-full text-sm font-semibold transition-colors cursor-pointer"
            >
              Send en ny besked
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot field (hidden from real users) */}
            <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
              <label htmlFor="website_url_underline">Please do not fill in this field</label>
              <input
                type="text"
                id="website_url_underline"
                name="website_url"
                value={formData.website_url}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            {/* Name input */}
            <div className="relative">
              <input
                type="text"
                id="underline_name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit navn *"
                className="w-full px-1 py-3.5 bg-transparent border-0 border-b-2 border-stone-700 focus:border-amber-500 text-white placeholder:text-stone-400 outline-none text-base transition-colors"
              />
            </div>

            {/* Phone & Email inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="relative">
                <input
                  type="tel"
                  id="underline_phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Telefonnummer *"
                  className="w-full px-1 py-3.5 bg-transparent border-0 border-b-2 border-stone-700 focus:border-amber-500 text-white placeholder:text-stone-400 outline-none text-base transition-colors"
                />
              </div>

              <div className="relative">
                <input
                  type="email"
                  id="underline_email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="E-mailadresse *"
                  className="w-full px-1 py-3.5 bg-transparent border-0 border-b-2 border-stone-700 focus:border-amber-500 text-white placeholder:text-stone-400 outline-none text-base transition-colors"
                />
              </div>
            </div>

            {/* Message input */}
            <div className="relative">
              <textarea
                id="underline_message"
                name="message"
                rows={4}
                required
                value={formData.message}
                onChange={handleChange}
                placeholder="Hvordan kan vi hjælpe dig? (beskriv din opgave) *"
                className="w-full px-1 py-3.5 bg-transparent border-0 border-b-2 border-stone-700 focus:border-amber-500 text-white placeholder:text-stone-400 outline-none text-base transition-colors resize-y"
              ></textarea>
            </div>

            {status === 'error' && (
              <div className="p-4 bg-red-950/60 border border-red-500/40 rounded-xl text-red-200 text-sm flex items-start gap-3">
                <i className="fa-solid fa-circle-exclamation mt-0.5 text-red-400" aria-hidden="true"></i>
                <span>{errorMessage}</span>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full sm:w-auto min-w-[220px] px-10 py-4 bg-amber-600 hover:bg-amber-500 text-white font-bold text-base rounded-full shadow-xl transition-all duration-200 flex items-center justify-center gap-3 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed group"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                    <span>Sender besked...</span>
                  </>
                ) : (
                  <>
                    <span>Send forespørgsel</span>
                    <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/80 p-6 sm:p-8 lg:p-10">
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
          Udfyld kontaktformularen her
        </h3>
        <p className="text-stone-600 text-sm mt-1">
          Send os en besked med din forespørgsel, så vender vi hurtigt tilbage.
        </p>
      </div>

      {status === 'success' ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
          <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3 text-xl">
            <i className="fa-solid fa-check" aria-hidden="true"></i>
          </div>
          <h4 className="text-lg font-bold text-emerald-900">Tak for din henvendelse!</h4>
          <p className="text-emerald-700 text-sm mt-1">
            Vi har modtaget din besked og kontakter dig hurtigst muligt.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field (hidden from real users) */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Please do not fill in this field</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-stone-900 mb-1.5">
              Navn <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:ring-2 focus:ring-amber-600 focus:border-amber-600 outline-none text-stone-900 bg-white placeholder:text-stone-400 text-sm transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-stone-900 mb-1.5">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="+45 12 34 56 78"
                className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:ring-2 focus:ring-amber-600 focus:border-amber-600 outline-none text-stone-900 bg-white placeholder:text-stone-400 text-sm transition-all"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-stone-900 mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:ring-2 focus:ring-amber-600 focus:border-amber-600 outline-none text-stone-900 bg-white placeholder:text-stone-400 text-sm transition-all"
              />
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-stone-900 mb-1.5">
              Beskrivelse <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="Beskriv din opgave eller dit projekt..."
              className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:ring-2 focus:ring-amber-600 focus:border-amber-600 outline-none text-stone-900 bg-white placeholder:text-stone-400 text-sm transition-all resize-y"
            ></textarea>
          </div>

          {status === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-3">
              <i className="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full sm:w-auto px-8 py-3.5 bg-stone-900 hover:bg-stone-800 text-white font-semibold text-sm rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                <span>Sender...</span>
              </>
            ) : (
              <>
                <span>Send besked</span>
                <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
